import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { PropsWithChildren, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import Exclamation from '@/assets/icons/exclamation.svg';
import LightningBolt from '@/assets/icons/lightning-bolt.svg';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';

export function Card({ children }: PropsWithChildren) {
  return (
    <div className="shadow-base flex flex-col items-start rounded-lg bg-white p-4">
      {children}
    </div>
  );
}

function SnapshotContent({
  showAggregateData,
}: {
  showAggregateData: boolean;
}) {
  const {
    data: {
      snapshots: {
        total_openings: totalOpenings,
        openings_at_risk: atRisk,
        active_sourcing_openings: activeSourcing,
      },
    },
  } = useSuspenseQuery({
    queryKey: ['dashboardSnapshot', showAggregateData],
    queryFn: () =>
      SourcingService.getInternalApiSourcingDashboardSnapshots(
        showAggregateData,
      ),
  });

  return (
    <>
      <div className="flex flex-row gap-4">
        <Card>
          <img src={Exclamation} alt="Exclamation" className="h-6 w-6" />
          <div className="py-2">
            <Typography variant="subtitle1">
              <FormattedMessage defaultMessage="Openings at risk" />
            </Typography>
            <Typography variant="large2">{`${atRisk} / ${totalOpenings}`}</Typography>
          </div>
        </Card>

        <Card>
          <img src={LightningBolt} alt="Lightning bolt" className="h-6 w-6" />
          <div className="py-2">
            <Typography variant="subtitle1">
              <FormattedMessage defaultMessage="Active sourcing" />
            </Typography>
            <Typography variant="large2">
              {`${activeSourcing} / ${totalOpenings}`}
            </Typography>
          </div>
        </Card>
      </div>
    </>
  );
}

export function Snapshot({
  showAggregateData,
}: {
  showAggregateData: boolean;
}) {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<Snapshot.Skeleton />}>
        <SnapshotContent showAggregateData={showAggregateData} />
      </Suspense>
    </ErrorBoundary>
  );
}

Snapshot.Skeleton = function SnapshotSkeleton() {
  return (
    <>
      <div className="flex flex-row gap-4">
        <Card>
          <img src={Exclamation} alt="Exclamation" className="h-6 w-6" />
          <div className="py-2">
            <Typography variant="subtitle1">
              <FormattedMessage defaultMessage="Openings at risk" />
            </Typography>
            <Skeleton className="h-6 w-12" />
          </div>
        </Card>

        <Card>
          <img src={LightningBolt} alt="Lightning bolt" className="h-6 w-6" />
          <div className="py-2">
            <Typography variant="subtitle1">
              <FormattedMessage defaultMessage="Active sourcing" />
            </Typography>
            <Skeleton className="h-6 w-12" />
          </div>
        </Card>
      </div>
    </>
  );
};
